import { useState, useEffect, useRef, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, MapPin, Clock, Download, ChevronLeft, ChevronRight, Star, ExternalLink, User, Facebook, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { translations } from "./translations";
import AdminApp from "./pages/admin/AdminApp";
import { DataProvider, useData } from "./contexts/DataContext";
import DOMPurify from "dompurify";

// Language Context
const LanguageContext = createContext();

const useLanguage = () => useContext(LanguageContext);

const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('de');
  
  const t = (key) => {
    if (!key || typeof key !== 'string') return key || '';
    const keys = key.split('.');
    let result = translations[language];
    for (const k of keys) {
      if (result && result[k] !== undefined) {
        result = result[k];
      } else {
        // Fallback to German
        result = translations['de'];
        for (const k2 of keys) {
          if (result && result[k2] !== undefined) {
            result = result[k2];
          } else {
            return key;
          }
        }
        break;
      }
    }
    return result;
  };
  
  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Assets
const ASSETS = {
  logo: "https://customer-assets.emergentagent.com/job_muresan-richter/artifacts/0z0k6bnj_Zahnarzt_Muresan_klein.png",
  practiceRoom: "https://customer-assets.emergentagent.com/job_muresan-richter/artifacts/8s0ivnrp_IMG_1112.jpg",
  practiceSlideshow: [
    "https://customer-assets.emergentagent.com/job_muresan-richter/artifacts/q8zxkglj_496858242_4090167401203582_1212811520728737465_n.jpg",
    "https://customer-assets.emergentagent.com/job_muresan-richter/artifacts/eofigukl_496858936_4090167347870254_7818876438373784746_n.jpg",
    "https://customer-assets.emergentagent.com/job_muresan-richter/artifacts/jjiu1433_Behandlung_1.jpg"
  ],
  drMuresan: "https://customer-assets.emergentagent.com/job_muresan-richter/artifacts/yk61p6e4_andrei-muresan-2017-06-02-02.jpg",
  frauHomann: "https://customer-assets.emergentagent.com/job_muresan-richter/artifacts/o9ay11ae_Zahnarztpraxis_Muresan_Richter_%20Homann___2023_03_21__%20002%20-%20Kopie.jpg",
  treatments: {
    aesthetik: "https://customer-assets.emergentagent.com/job_muresan-richter/artifacts/71ghwbt8_A%CC%88sthetischeZahnmedizin.png",
    pzr: "https://customer-assets.emergentagent.com/job_muresan-richter/artifacts/pygvqqgv_Professionelle%20Zahnreinigung.png",
    implantologie: "https://customer-assets.emergentagent.com/job_muresan-richter/artifacts/orwhqn59_Zahnimplantate.png",
    kiefergelenk: "https://customer-assets.emergentagent.com/job_muresan-richter/artifacts/cpiyjxr2_Kiefergelenkprobleme.png",
    vollkeramik: "https://customer-assets.emergentagent.com/job_muresan-richter/artifacts/z924bhq4_Vollkeramik.png",
    parodontitis: "https://customer-assets.emergentagent.com/job_muresan-richter/artifacts/3te4oev7_Parodontitisbehandlung.png",
    veneers: "https://customer-assets.emergentagent.com/job_muresan-richter/artifacts/wca3qzdw_Veneers.png"
  }
};

const CONTACT = {
  name: "Zahnarztpraxis Andrei Günter Muresan-Richter",
  phone: "+49 (0) 8131 / 12122",
  email: "info@zahnarzt-muresan.de",
  jobEmail: "Bewerbung@zahnarzt-muresan.de",
  address: "Stresemannstraße 13, 85221 Dachau",
  hours: {
    monday: "Mo: 08:30 - 12:00, 14:00 - 18:00 Uhr",
    tuesday: "Di: 08:30 - 12:00, 14:00 - 18:00 Uhr",
    wednesday: "Mi: 08:30 - 12:00, 14:00 - 18:00 Uhr",
    thursday: "Do: 08:30 - 12:00, 14:00 - 18:00 Uhr",
    friday: "Fr: 08:30 - 12:00 Uhr"
  },
  jobEmail: "bewerbung@zahnarzt-dachau.com",
  doctolib: "https://www.doctolib.de/zahnarztpraxis/dachau/zahnarztpraxis-andrei-guenter-muresan-richter",
  googleMaps: "https://share.google/4a1bKvHUyP4cOiEyC",
  googleReviews: "https://g.page/r/CSob0dA4Vw0rEBM/review",
  facebook: "https://www.facebook.com/zahnarztmuresan",
  instagram: "https://www.instagram.com/zahnarztpraxis_muresan_richter/"
};

// Cookie Consent Context
const CookieContext = createContext();

const useCookies = () => useContext(CookieContext);

// Google Tag Manager ID
const GTM_ID = 'G-XLB1Z1BT5Y';

// Function to load Google Tag Manager
const loadGoogleTagManager = () => {
  // Check if already loaded
  if (window.gtag) return;
  
  // Create gtag script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GTM_ID}`;
  document.head.appendChild(script);
  
  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GTM_ID);
};

const CookieProvider = ({ children }) => {
  const [cookieConsent, setCookieConsent] = useState(null);
  const [showBanner, setShowBanner] = useState(true);

  useEffect(() => {
    const consent = localStorage.getItem("cookie_consent");
    if (consent) {
      const parsedConsent = JSON.parse(consent);
      setCookieConsent(parsedConsent);
      setShowBanner(false);
      
      // Load GTM if analytics was previously accepted
      if (parsedConsent.analytics) {
        loadGoogleTagManager();
      }
    }
  }, []);

  const acceptAll = () => {
    const consent = { essential: true, analytics: true, marketing: true };
    localStorage.setItem("cookie_consent", JSON.stringify(consent));
    setCookieConsent(consent);
    setShowBanner(false);
    
    // Load Google Tag Manager when user accepts
    loadGoogleTagManager();
  };

  const acceptEssential = () => {
    const consent = { essential: true, analytics: false, marketing: false };
    localStorage.setItem("cookie_consent", JSON.stringify(consent));
    setCookieConsent(consent);
    setShowBanner(false);
    // Do NOT load GTM when only essential cookies are accepted
  };

  return (
    <CookieContext.Provider value={{ cookieConsent, showBanner, acceptAll, acceptEssential }}>
      {children}
    </CookieContext.Provider>
  );
};

// Cookie Consent Banner
const CookieConsent = () => {
  const { showBanner, acceptAll, acceptEssential } = useCookies();
  const { t } = useLanguage();

  if (!showBanner) return null;

  return (
    <>
      <div className="cookie-overlay" aria-hidden="true" />
      <div 
        className="cookie-modal" 
        role="dialog" 
        aria-modal="true" 
        aria-labelledby="cookie-title"
        data-testid="cookie-consent-modal"
      >
        <h2 id="cookie-title" className="text-xl font-semibold mb-4" style={{ color: 'var(--primary)' }}>
          {t('cookies.title')}
        </h2>
        <p className="text-gray-600 mb-6 text-sm leading-relaxed">
          {t('cookies.text')}{" "}
          <Link to="/datenschutz" className="underline" style={{ color: 'var(--primary)' }}>
            {t('cookies.privacyLink')}
          </Link>.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button 
            onClick={acceptAll} 
            className="btn-primary flex-1"
            data-testid="cookie-accept-all"
          >
            {t('cookies.acceptAll')}
          </Button>
          <Button 
            onClick={acceptEssential} 
            variant="outline"
            className="btn-secondary flex-1"
            data-testid="cookie-accept-essential"
          >
            {t('cookies.acceptEssential')}
          </Button>
        </div>
      </div>
    </>
  );
};

// Neuigkeiten Popup - Dynamisch aus Admin Panel
const NewsPopup = () => {
  const { activePopup } = useData();
  const [showNews, setShowNews] = useState(true);
  
  useEffect(() => {
    const dismissed = sessionStorage.getItem("news_dismissed");
    if (dismissed) {
      setShowNews(false);
    }
  }, []);

  const closeNews = () => {
    sessionStorage.setItem("news_dismissed", "true");
    setShowNews(false);
  };

  // Don't show if no active popup or already dismissed
  if (!activePopup || !showNews) return null;

  return (
    <div 
      className="fixed bottom-4 right-4 z-40 max-w-md bg-white rounded-2xl shadow-2xl p-6 border-2 overflow-y-auto max-h-[85vh]"
      style={{ borderColor: 'var(--primary)' }}
      role="alert"
      data-testid="news-popup"
    >
      <button 
        onClick={closeNews}
        className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-100"
        aria-label="Schließen"
      >
        <X className="w-5 h-5" style={{ color: 'var(--secondary)' }} />
      </button>
      <div className="text-left pr-4">
        {activePopup.title && (
          <h3 className="text-lg font-semibold mb-3" style={{ color: 'var(--primary)' }}>
            {activePopup.title}
          </h3>
        )}
        <div 
          className="text-sm popup-content"
          style={{ color: 'var(--text-secondary)' }}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(activePopup.content) }}
        />
      </div>
    </div>
  );
};

// Header Component
const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showJobsMenu, setShowJobsMenu] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();

  const languages = [
    { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'hu', label: 'Magyar', flag: '🇭🇺' }
  ];

  const navItems = [
    { label: t('nav.contact'), href: "/#kontakt" },
    { label: t('nav.treatments'), href: "/#behandlungsfelder" },
    { label: t('nav.team'), href: "/#team", hasSubmenu: true },
    { label: t('nav.directions'), href: "/#anfahrt" }
  ];

  const scrollToSection = (href) => {
    setIsOpen(false);
    setShowJobsMenu(false);
    if (href.startsWith("/#")) {
      const id = href.replace("/#", "");
      if (location.pathname === "/") {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      } else {
        window.location.href = href;
      }
    }
  };

  const openJobModal = () => {
    setShowJobsMenu(false);
    // Scroll to team section and open job modal
    const teamSection = document.getElementById("team");
    if (teamSection) {
      teamSection.scrollIntoView({ behavior: "smooth" });
      // Trigger job modal after scroll
      setTimeout(() => {
        const jobMember = document.querySelector('[data-testid="team-member-4"]');
        if (jobMember) jobMember.click();
      }, 500);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-gray-200/50">
      <div className="container mx-auto px-4 md:px-8">
        <nav className="flex items-center justify-between h-20" role="navigation" aria-label="Hauptnavigation">
          <Link 
            to="/" 
            className="flex items-center gap-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            style={{ '--tw-ring-color': 'var(--primary)' }}
            aria-label="Startseite - Zahnarztpraxis Muresan-Richter"
            data-testid="logo-link"
          >
            <img 
              src={ASSETS.logo} 
              alt="Zahnarztpraxis Muresan-Richter Logo" 
              className="h-12 w-12 rounded-full"
            />
            <span className="font-semibold text-sm sm:text-base md:text-lg" style={{ color: 'var(--primary)' }}>
              Zahnarztpraxis Muresan-Richter
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {/* Language Selector */}
            <div 
              className="relative"
              onMouseEnter={() => setShowLangMenu(true)}
              onMouseLeave={() => setShowLangMenu(false)}
            >
              <button
                className="flex items-center gap-1 text-sm font-medium transition-colors hover:opacity-70 px-2 py-1 rounded"
                style={{ color: 'var(--text-secondary)' }}
                data-testid="lang-selector"
              >
                <span>{languages.find(l => l.code === language)?.flag}</span>
                <span>{languages.find(l => l.code === language)?.code.toUpperCase()}</span>
              </button>
              {showLangMenu && (
                <div 
                  className="absolute top-full right-0 mt-1 bg-white rounded-lg shadow-lg py-2 min-w-32 z-50"
                  style={{ borderColor: 'var(--primary-light)', borderWidth: '1px' }}
                >
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        setLanguage(lang.code);
                        setShowLangMenu(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm font-medium hover:bg-gray-50 flex items-center gap-2 ${language === lang.code ? 'bg-gray-50' : ''}`}
                      style={{ color: 'var(--text-secondary)' }}
                      data-testid={`lang-${lang.code}`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {navItems.map((item) => (
              item.hasSubmenu ? (
                <div 
                  key={item.label} 
                  className="relative"
                  onMouseEnter={() => setShowJobsMenu(true)}
                  onMouseLeave={() => setShowJobsMenu(false)}
                >
                  <button
                    onClick={() => scrollToSection(item.href)}
                    className="text-sm font-medium transition-colors hover:opacity-70"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {item.label}
                  </button>
                  {showJobsMenu && (
                    <div 
                      className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg py-2 min-w-32"
                      style={{ borderColor: 'var(--primary-light)', borderWidth: '1px' }}
                    >
                      <button
                        onClick={openJobModal}
                        className="w-full text-left px-4 py-2 text-sm font-medium hover:bg-gray-50"
                        style={{ color: 'var(--text-secondary)' }}
                        data-testid="nav-jobs"
                      >
                        {t('nav.jobs')}
                      </button>
                    </div>
                  )}
                </div>
              ) : item.href.startsWith("/#") ? (
                <button
                  key={item.label}
                  onClick={() => scrollToSection(item.href)}
                  className="text-sm font-medium transition-colors hover:opacity-70"
                  style={{ color: 'var(--text-secondary)' }}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </button>
              ) : (
                <Link
                  key={item.label}
                  to={item.href}
                  className="text-sm font-medium transition-colors hover:opacity-70"
                  style={{ color: 'var(--text-secondary)' }}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </Link>
              )
            ))}
            <a 
              href={`tel:${CONTACT.phone.replace(/\s/g, '')}`}
              className="btn-secondary text-sm py-2 px-4"
              data-testid="header-call-btn"
            >
              <Phone className="w-4 h-4" />
              {t('nav.call')}
            </a>
            <a 
              href={CONTACT.doctolib} 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-primary text-sm py-2 px-4"
              data-testid="header-booking-btn"
            >
              {t('nav.bookNow')}
            </a>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <button
                className="lg:hidden p-2 flex flex-col gap-1.5"
                aria-label="Menü öffnen"
                data-testid="hamburger-menu"
              >
                <span className="hamburger-line" />
                <span className="hamburger-line" />
                <span className="hamburger-line" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px]">
              <nav className="flex flex-col gap-4 mt-8">
                {/* Mobile Language Selector */}
                <div className="flex gap-2 mb-4 pb-4 border-b" style={{ borderColor: 'var(--primary-light)' }}>
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => setLanguage(lang.code)}
                      className={`flex items-center gap-1 px-3 py-2 rounded-full text-sm font-medium transition-colors ${language === lang.code ? 'bg-[var(--primary-light)]' : 'hover:bg-gray-100'}`}
                      style={{ color: language === lang.code ? 'var(--primary)' : 'var(--text-secondary)' }}
                      data-testid={`mobile-lang-${lang.code}`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.code.toUpperCase()}</span>
                    </button>
                  ))}
                </div>
                
                {navItems.map((item) => (
                  <div key={item.label}>
                    {item.href.startsWith("/#") ? (
                      <button
                        onClick={() => scrollToSection(item.href)}
                        className="text-lg font-medium py-2 text-left w-full"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        {item.label}
                      </button>
                    ) : (
                      <Link
                        to={item.href}
                        onClick={() => setIsOpen(false)}
                        className="text-lg font-medium py-2 block"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        {item.label}
                      </Link>
                    )}
                    {item.hasSubmenu && (
                      <button
                        onClick={() => {
                          setIsOpen(false);
                          openJobModal();
                        }}
                        className="text-base py-1 pl-4 text-left w-full"
                        style={{ color: 'var(--secondary)' }}
                        data-testid="mobile-nav-jobs"
                      >
                        → {t('nav.jobs')}
                      </button>
                    )}
                  </div>
                ))}
                <a 
                  href={`tel:${CONTACT.phone.replace(/\s/g, '')}`}
                  className="btn-secondary mt-4 text-center"
                  data-testid="mobile-call-btn"
                >
                  <Phone className="w-5 h-5" />
                  {t('nav.call')}
                </a>
                <a 
                  href={CONTACT.doctolib} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-primary text-center"
                  data-testid="mobile-booking-btn"
                >
                  {t('nav.bookNow')}
                </a>
              </nav>
            </SheetContent>
          </Sheet>
        </nav>
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { t } = useLanguage();
  const { contact } = useData();
  
  // Use backend data or fallback
  const contactData = contact || CONTACT;

  const downloadVCard = () => {
    const phone = contactData.phone || CONTACT.phone;
    const email = contactData.email || CONTACT.email;
    const address = contactData.address || CONTACT.address;
    const name = contactData.practice_name || CONTACT.name;
    
    const vcard = `BEGIN:VCARD
VERSION:3.0
FN:${name}
ORG:Zahnarztpraxis Muresan-Richter
TEL;TYPE=WORK,VOICE:${phone.replace(/[^\d+]/g, '')}
EMAIL:${email}
ADR;TYPE=WORK:;;${address}
URL:https://www.zahnarzt-muresan.de
END:VCARD`;
    
    const blob = new Blob([vcard], { type: 'text/vcard' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'zahnarztpraxis-muresan-richter.vcf';
    link.click();
    URL.revokeObjectURL(url);
  };

  // Auto-advance slideshow
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % ASSETS.practiceSlideshow.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % ASSETS.practiceSlideshow.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + ASSETS.practiceSlideshow.length) % ASSETS.practiceSlideshow.length);
  };

  return (
    <section 
      id="kontakt" 
      className="min-h-screen pt-24 pb-16 md:pt-32 md:pb-24"
      style={{ background: 'var(--background-slide-1)' }}
      aria-labelledby="hero-title"
    >
      <div className="container mx-auto px-4 md:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="animate-fadeInUp">
            <p className="uppercase text-xs tracking-widest font-semibold mb-4" style={{ color: 'var(--secondary)' }}>
              {t('hero.welcome')}
            </p>
            <h1 
              id="hero-title"
              className="text-4xl md:text-5xl lg:text-6xl font-light tracking-tight mb-6"
              style={{ color: 'var(--primary)' }}
            >
              <span className="font-bold">Zahnarzt Dachau</span><br />
              <span className="font-medium">{t('hero.title2')}</span>
            </h1>
            <p className="text-lg md:text-xl leading-relaxed mb-6" style={{ color: 'var(--text-secondary)' }}>
              {t('hero.description')}
            </p>

            {/* Unsere Leistungen Button - after description */}
            <div className="mb-8">
              <a 
                href="#behandlungsfelder"
                className="btn-secondary inline-flex"
                data-testid="hero-treatments-btn"
              >
                {t('hero.services')}
              </a>
            </div>

            {/* Contact Info */}
            <div className="space-y-4 mb-8">
              <a 
                href={`tel:${(contactData.phone || CONTACT.phone).replace(/\s/g, '')}`}
                className="flex items-center gap-3 text-lg hover:opacity-70 transition-opacity"
                style={{ color: 'var(--text-primary)' }}
                data-testid="contact-phone"
              >
                <Phone className="w-5 h-5" style={{ color: 'var(--primary)' }} aria-hidden="true" />
                <span>{contactData.phone || CONTACT.phone}</span>
              </a>
              <a 
                href={`mailto:${contactData.email || CONTACT.email}`}
                className="flex items-center gap-3 text-lg hover:opacity-70 transition-opacity"
                style={{ color: 'var(--text-primary)' }}
                data-testid="contact-email"
              >
                <Mail className="w-5 h-5" style={{ color: 'var(--primary)' }} aria-hidden="true" />
                <span>{contactData.email || CONTACT.email}</span>
              </a>
              <div className="flex items-start gap-3 text-lg" style={{ color: 'var(--text-primary)' }}>
                <MapPin className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: 'var(--primary)' }} aria-hidden="true" />
                <span>{contactData.address || CONTACT.address}</span>
              </div>
              <div className="flex items-start gap-3" style={{ color: 'var(--text-primary)' }}>
                <Clock className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: 'var(--primary)' }} aria-hidden="true" />
                <div className="text-sm">
                  {(() => {
                    const hours = contactData.opening_hours || contactData.hours;
                    if (hours && Array.isArray(hours)) {
                      return hours.map((hour, index) => (
                        <p key={hour.id || index}>{hour.value}</p>
                      ));
                    }
                    return (
                      <table className="border-collapse">
                        <tbody>
                          <tr><td className="pr-4 text-left">Mo:</td><td>08:30 - 12:00, 14:00 - 18:00 Uhr</td></tr>
                          <tr><td className="pr-4 text-left">Di:</td><td>08:30 - 12:00, 14:00 - 18:00 Uhr</td></tr>
                          <tr><td className="pr-4 text-left">Mi:</td><td>08:30 - 12:00, 14:00 - 18:00 Uhr</td></tr>
                          <tr><td className="pr-4 text-left">Do:</td><td>08:30 - 12:00, 14:00 - 18:00 Uhr</td></tr>
                          <tr><td className="pr-4 text-left">Fr:</td><td>08:30 - 12:00 Uhr</td></tr>
                        </tbody>
                      </table>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Booking Buttons - Anrufen and Direkt zum Termin */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href={`tel:${(contactData.phone || CONTACT.phone).replace(/\s/g, '')}`}
                className="btn-secondary"
                data-testid="hero-call-btn"
              >
                <Phone className="w-5 h-5" />
                {t('nav.call')}
              </a>
              <a 
                href={contactData.doctolib_link || CONTACT.doctolib}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
                data-testid="hero-booking-btn"
              >
                {t('nav.bookNow')}
              </a>
            </div>
            
            {/* Contact Save Button */}
            <div className="mt-4">
              <button 
                onClick={downloadVCard}
                className="btn-secondary text-sm py-2 px-4"
                data-testid="hero-contact-save-btn"
              >
                <Download className="w-4 h-4" />
                {t('hero.saveContact')}
              </button>
            </div>
          </div>

          {/* Right Content - Image Slideshow */}
          <div className="animate-fadeInUp animation-delay-200">
            <div className="relative rounded-3xl shadow-2xl overflow-hidden">
              {/* Slideshow Images */}
              <div className="relative h-[400px] md:h-[500px] lg:h-[600px]">
                {ASSETS.practiceSlideshow.map((image, index) => (
                  <img 
                    key={index}
                    src={image}
                    alt={`Praxisansicht ${index + 1}`}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                      index === currentSlide ? 'opacity-100' : 'opacity-0'
                    }`}
                    data-testid={`slideshow-image-${index}`}
                  />
                ))}
              </div>

              {/* Slideshow Navigation Arrows */}
              <button 
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 hover:bg-white transition-colors shadow-lg"
                aria-label="Vorheriges Bild"
                data-testid="slideshow-prev"
              >
                <ChevronLeft className="w-6 h-6" style={{ color: 'var(--primary)' }} />
              </button>
              <button 
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 hover:bg-white transition-colors shadow-lg"
                aria-label="Nächstes Bild"
                data-testid="slideshow-next"
              >
                <ChevronRight className="w-6 h-6" style={{ color: 'var(--primary)' }} />
              </button>

              {/* Slideshow Dots */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {ASSETS.practiceSlideshow.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      index === currentSlide 
                        ? 'bg-white w-6' 
                        : 'bg-white/50 hover:bg-white/70'
                    }`}
                    aria-label={`Zu Bild ${index + 1}`}
                    data-testid={`slideshow-dot-${index}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const { t } = useLanguage();
  
  return (
    <section 
      className="section"
      style={{ background: 'var(--background-slide-2)' }}
      aria-labelledby="about-title"
    >
      <div className="container mx-auto">
        <div className="max-w-4xl mx-auto text-center">
          <h1 
            id="about-title"
            className="text-3xl md:text-4xl font-medium tracking-tight mb-8"
            style={{ color: 'var(--primary)' }}
          >
            {t('about.title')}
          </h1>
          <p 
            className="text-lg leading-relaxed mb-6 text-left" 
            style={{ color: 'var(--text-secondary)' }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(t('about.text1')) }}
          />
          <p className="text-lg leading-relaxed mb-8 text-left" style={{ color: 'var(--text-secondary)' }}>
            {t('about.text2')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href={`tel:${CONTACT.phone.replace(/\s/g, '')}`}
              className="btn-secondary inline-flex"
              data-testid="about-call-btn"
            >
              <Phone className="w-5 h-5" />
              {t('about.callBtn')}
            </a>
            <a 
              href={CONTACT.doctolib}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex"
              data-testid="about-booking-btn"
            >
              {t('about.bookBtn')}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Treatment Data - Fallback
const TREATMENTS_FALLBACK = [
  {
    id: "aesthetik",
    title: "Ästhetische Zahnheilkunde",
    subtitle: "Ihr schönstes Lächeln in besten Händen",
    image: ASSETS.treatments.aesthetik,
    description: "Ein strahlendes Lächeln ist mehr als nur schön – es ist Ausdruck von Gesundheit, Selbstbewusstsein und Lebensfreude. Wir verbinden moderne Zahnmedizin mit einem besonderen Blick für Ästhetik.",
    services: [
      "Professionelle Zahnaufhellung (Bleaching)",
      "Veneers und Keramik-Inlays",
      "Zahnfarbene Füllungen und Restaurationen",
      "Zahnform- und Zahnstellungskorrekturen"
    ]
  },
  {
    id: "prophylaxe",
    title: "Prophylaxe (PZR)",
    subtitle: "Für Ihr strahlendes und gesundes Lächeln",
    image: ASSETS.treatments.pzr,
    description: "Die eigenen Zähne sind immer besser als jeglicher Zahnersatz. Einer der wichtigsten Bausteine der Zahnerhaltung ist die Prophylaxe.",
    services: [
      "Schonende Entfernung von Zahnbelag und Verfärbungen",
      "Regelmäßige Untersuchung durch den Zahnarzt",
      "Kontrolle Ihrer Mundgesundheit",
      "Wertvolle Tipps für die tägliche Pflege zu Hause"
    ]
  },
  {
    id: "implantologie",
    title: "Implantologie",
    subtitle: "Natürlicher Zahnersatz für mehr Lebensqualität",
    image: ASSETS.treatments.implantologie,
    description: "In der heutigen Zeit muss sich kein Mensch mehr mit fehlenden Zähnen abfinden. Funktionelle und optische Einschränkungen durch Zahnlücken mindern die Lebensqualität erheblich.",
    services: [
      "Individuelle Implantatplanung",
      "Minimalinvasive Eingriffe",
      "Hochwertige Implantatsysteme",
      "Umfassende Nachsorge"
    ]
  },
  {
    id: "kiefergelenk",
    title: "Kiefergelenksprobleme",
    subtitle: "Hilfe bei Kieferknacken und Schmerzen",
    image: ASSETS.treatments.kiefergelenk,
    description: "Haben Sie Kieferknacken, Schmerzen beim Kauen oder Verspannungen im Nackenbereich? Häufig sind diese Symptome auf Stress, Fehlhaltungen oder Zahnfehlstellungen zurückzuführen.",
    services: [
      "Diagnose von Kiefergelenksstörungen",
      "Individuell angepasste Knirschschienen",
      "Therapie bei Bruxismus",
      "Ganzheitliche Behandlungsansätze"
    ]
  },
  {
    id: "vollkeramik",
    title: "Vollkeramik",
    subtitle: "Natürliche Ästhetik und höchste Qualität",
    image: ASSETS.treatments.vollkeramik,
    description: "Vollkeramische Versorgungen bieten eine natürliche Ästhetik und höchste Biokompatibilität für anspruchsvolle Zahnrestaurationen.",
    services: [
      "Keramikkronen und -brücken",
      "Inlays und Onlays",
      "Metallfreie Restaurationen",
      "Höchste Präzision und Langlebigkeit"
    ]
  },
  {
    id: "parodontitis",
    title: "Parodontitisbehandlung",
    subtitle: "Für ein gesundes Zahnfleisch",
    image: ASSETS.treatments.parodontitis,
    description: "Parodontitis ist eine chronische Entzündung des Zahnhalteapparates, die unbehandelt zu Zahnlockerung oder sogar Zahnverlust führen kann.",
    services: [
      "Gründliche Diagnose",
      "Sanfte Reinigung unterhalb des Zahnfleischrandes",
      "Individuelle Nachsorge",
      "Regelmäßige Kontrollen"
    ]
  },
  {
    id: "veneers",
    title: "Veneers",
    subtitle: "Hauchdünne Keramikschalen für perfekte Zähne",
    image: ASSETS.treatments.veneers,
    description: "Veneers sind hauchdünne Keramikschalen, die auf die Frontzähne geklebt werden und kleine Makel perfekt kaschieren.",
    services: [
      "Minimalinvasive Präparation",
      "Natürliche Farbgestaltung",
      "Langlebige Ergebnisse",
      "Individuelle Formgebung"
    ]
  }
];

// Treatment Slider
const TreatmentSlider = () => {
  const sliderRef = useRef(null);
  const [selectedTreatment, setSelectedTreatment] = useState(null);
  const { t, language } = useLanguage();
  const { treatments: treatmentsFromApi } = useData();

  // Use API data if available, otherwise fallback
  const treatmentsData = treatmentsFromApi && treatmentsFromApi.length > 0 ? treatmentsFromApi : TREATMENTS_FALLBACK;

  const scroll = (direction) => {
    if (sliderRef.current) {
      const scrollAmount = 350;
      sliderRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  
  // Get translated treatment data
  const getTranslatedTreatment = (treatment) => {
    const translatedItem = t(`treatments.items.${treatment.id}`);
    if (typeof translatedItem === 'object') {
      return {
        ...treatment,
        title: translatedItem.title || treatment.title,
        subtitle: translatedItem.subtitle || treatment.subtitle,
        description: translatedItem.description || treatment.description,
        services: translatedItem.services || treatment.services
      };
    }
    return treatment;
  };

  return (
    <section 
      id="behandlungsfelder" 
      className="section"
      style={{ background: 'var(--background-slide-3)' }}
      aria-labelledby="treatments-title"
    >
      <div className="container mx-auto">
        <div className="flex justify-between items-end mb-12">
          <div>
            <p className="uppercase text-xs tracking-widest font-semibold mb-2" style={{ color: 'var(--secondary)' }}>
              {t('treatments.subtitle')}
            </p>
            <h2 
              id="treatments-title"
              className="text-3xl md:text-4xl font-medium tracking-tight"
              style={{ color: 'var(--primary)' }}
            >
              {t('treatments.title')}
            </h2>
          </div>
          <div className="hidden md:flex gap-2">
            <button 
              onClick={() => scroll('left')}
              className="p-3 rounded-full border-2 transition-colors hover:bg-white"
              style={{ borderColor: 'var(--primary)', color: 'var(--primary)' }}
              aria-label="Vorherige Behandlungen"
              data-testid="treatment-slider-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button 
              onClick={() => scroll('right')}
              className="p-3 rounded-full border-2 transition-colors hover:bg-white"
              style={{ borderColor: 'var(--primary)', color: 'var(--primary)' }}
              aria-label="Nächste Behandlungen"
              data-testid="treatment-slider-next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Slider */}
        <div 
          ref={sliderRef}
          className="treatment-slider"
          role="list"
          aria-label="Behandlungsfelder"
        >
          {treatmentsData.map((treatment) => {
            const translatedTreatment = getTranslatedTreatment(treatment);
            return (
              <article 
                key={treatment.id}
                className="treatment-card cursor-pointer"
                onClick={() => setSelectedTreatment(treatment)}
                role="listitem"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setSelectedTreatment(treatment)}
                data-testid={`treatment-card-${treatment.id}`}
              >
                <img 
                  src={treatment.image}
                  alt={`${translatedTreatment.title}`}
                  className="w-full h-48 object-cover rounded-xl mb-4"
                />
                <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--primary)' }}>
                  {translatedTreatment.title}
                </h3>
                <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                  {translatedTreatment.subtitle}
                </p>
                <button 
                  className="text-sm font-semibold flex items-center gap-2 transition-opacity hover:opacity-70"
                  style={{ color: 'var(--primary)' }}
                >
                  {t('treatments.learnMore')}
                  <ChevronRight className="w-4 h-4" />
                </button>
              </article>
            );
          })}
        </div>

        {/* Hint text for mobile users */}
        <p 
          className="text-center mt-6 text-sm"
          style={{ color: 'var(--secondary)' }}
          data-testid="treatment-hint"
        >
          {t('treatments.hint')}
        </p>

        {/* Treatment Detail Modal */}
        {selectedTreatment && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="treatment-modal-title"
          >
            <div 
              className="absolute inset-0 bg-black/50"
              onClick={() => setSelectedTreatment(null)}
            />
            <div className="relative bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8">
              <button 
                onClick={() => setSelectedTreatment(null)}
                className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100"
                aria-label="Schließen"
                data-testid="treatment-modal-close"
              >
                <X className="w-6 h-6" />
              </button>
              <img 
                src={selectedTreatment.image}
                alt={getTranslatedTreatment(selectedTreatment).title}
                className="w-full h-64 object-cover rounded-2xl mb-6"
              />
              <h3 
                id="treatment-modal-title"
                className="text-2xl font-semibold mb-2"
                style={{ color: 'var(--primary)' }}
              >
                {getTranslatedTreatment(selectedTreatment).title}
              </h3>
              <p className="text-lg mb-4" style={{ color: 'var(--secondary)' }}>
                {getTranslatedTreatment(selectedTreatment).subtitle}
              </p>
              <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>
                {getTranslatedTreatment(selectedTreatment).description}
              </p>
              <h4 className="font-semibold mb-3" style={{ color: 'var(--primary)' }}>
                Ihre Vorteile bei uns in <strong>Dachau</strong>:
              </h4>
              <ul className="space-y-2 mb-8">
                {getTranslatedTreatment(selectedTreatment).services.map((service, index) => (
                  <li key={index} className="flex items-start gap-2" style={{ color: 'var(--text-secondary)' }}>
                    <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ background: 'var(--primary)' }} />
                    {service}
                  </li>
                ))}
              </ul>
              <a 
                href={CONTACT.doctolib}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary w-full justify-center"
                data-testid="treatment-modal-booking"
              >
                Jetzt Beratungstermin in Dachau vereinbaren
              </a>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

// Team Section - Fallback data
const TEAM_FALLBACK = [
  {
    name: "Andrei Günter Muresan-Richter",
    role: "Zahnarzt",
    image: ASSETS.drMuresan,
    is_job_listing: false
  },
  {
    name: "Rita Raschdorf",
    role: "Assistenzärztin",
    image: null,
    is_job_listing: false
  },
  {
    name: "Mandy Homann",
    role: "ZMV/Stuhlassistenz",
    image: ASSETS.frauHomann,
    is_job_listing: false
  },
  {
    name: "Diana Siebenhüner",
    role: "Prophylaxe",
    image: null,
    is_job_listing: false
  },
  {
    name: "DU?",
    role: "Werde Teil unseres Teams!",
    image: null,
    is_job_listing: true
  }
];

const TeamSection = () => {
  const [showJobModal, setShowJobModal] = useState(false);
  const { t } = useLanguage();
  const { team: teamFromApi } = useData();
  
  // Use API data if available, otherwise fallback
  const teamData = teamFromApi && teamFromApi.length > 0 ? teamFromApi : TEAM_FALLBACK;
  
  // Get translated role for team member
  const getTranslatedRole = (member) => {
    const isJobListing = member.is_job_listing || member.isJobListing;
    if (isJobListing) {
      return t('team.jobPosting');
    }
    const roleKey = {
      'Zahnarzt': 'team.roles.dentist',
      'Assistenzärztin': 'team.roles.assistantDoctor',
      'ZMV/Stuhlassistenz': 'team.roles.zmv',
      'Stuhlassistenz': 'team.roles.chairAssistant',
      'Prophylaxe': 'team.roles.prophylaxis'
    };
    return t(roleKey[member.role]) || member.role;
  };

  // Helper to check if member is job listing
  const isJobListing = (member) => member.is_job_listing || member.isJobListing;

  // Helper to get initials safely
  const getInitials = (name) => {
    if (!name) return '??';
    return name.split(' ').map(n => n[0] || '').join('').slice(0, 2) || '??';
  };

  return (
    <section 
      id="team" 
      className="section"
      style={{ background: 'var(--background-slide-4)' }}
      aria-labelledby="team-title"
    >
      <div className="container mx-auto">
        <div className="text-center mb-12">
          <p className="uppercase text-xs tracking-widest font-semibold mb-2" style={{ color: 'var(--secondary)' }}>
            {t('team.subtitle')}
          </p>
          <h2 
            id="team-title"
            className="text-3xl md:text-4xl font-medium tracking-tight"
            style={{ color: 'var(--primary)' }}
          >
            {t('team.title')}
          </h2>
        </div>
        <div className="team-carousel">
          {teamData.map((member, index) => (
            <div 
              key={member.id || member.name || index}
              className={`team-member ${isJobListing(member) ? 'cursor-pointer' : ''}`}
              data-testid={`team-member-${index}`}
              onClick={() => isJobListing(member) && setShowJobModal(true)}
              onKeyDown={(e) => isJobListing(member) && e.key === 'Enter' && setShowJobModal(true)}
              tabIndex={isJobListing(member) ? 0 : undefined}
              role={isJobListing(member) ? 'button' : undefined}
            >
              {member.image ? (
                <img 
                  src={member.image}
                  alt={`${member.name || 'Team'}, ${getTranslatedRole(member)}`}
                  className="team-member-image"
                />
              ) : isJobListing(member) ? (
                <div 
                  className="team-member-image flex items-center justify-center border-4 border-dashed relative"
                  style={{ 
                    background: 'var(--primary-light)', 
                    color: 'var(--primary)',
                    borderColor: 'var(--primary)'
                  }}
                  aria-label="Stellenangebot"
                >
                  <User className="w-20 h-20 opacity-50" />
                  <span className="absolute text-3xl font-bold">?</span>
                </div>
              ) : (
                <div 
                  className="team-member-image flex items-center justify-center text-3xl font-semibold"
                  style={{ background: 'var(--primary-light)', color: 'var(--primary)' }}
                  aria-label={`Platzhalter für ${member.name || 'Team-Mitglied'}`}
                >
                  {getInitials(member.name)}
                </div>
              )}
              <h3 className="mt-4 font-semibold text-lg" style={{ color: 'var(--primary)' }}>
                {member.name || 'Team-Mitglied'}
              </h3>
              <p style={{ color: 'var(--text-secondary)' }}>
                {getTranslatedRole(member)}
              </p>
              {isJobListing(member) && (
                <button 
                  className="mt-2 text-sm font-semibold flex items-center gap-1 mx-auto"
                  style={{ color: 'var(--primary)' }}
                >
                  {t('team.toJobPosting')}
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Job Listing Modal */}
      {showJobModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="job-modal-title"
        >
          <div 
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowJobModal(false)}
          />
          <div className="relative bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8">
            <button 
              onClick={() => setShowJobModal(false)}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100"
              aria-label="Schließen"
              data-testid="job-modal-close"
            >
              <X className="w-6 h-6" />
            </button>
            
            <div className="text-center mb-6">
              <div 
                className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 relative"
                style={{ background: 'var(--primary-light)', color: 'var(--primary)' }}
              >
                <User className="w-12 h-12 opacity-50" />
                <span className="absolute text-2xl font-bold">?</span>
              </div>
              <h3 
                id="job-modal-title"
                className="text-2xl font-semibold mb-2"
                style={{ color: 'var(--primary)' }}
              >
                {t('job.title')}
              </h3>
              <p style={{ color: 'var(--secondary)' }}>
                {t('job.subtitle')}
              </p>
            </div>

            <div className="space-y-6">
              <div>
                <h4 className="font-semibold mb-3" style={{ color: 'var(--primary)' }}>
                  {t('job.tasksTitle')}
                </h4>
                <ul className="space-y-2">
                  {(t('job.tasks') || []).map((task, index) => (
                    <li key={index} className="flex items-start gap-2" style={{ color: 'var(--text-secondary)' }}>
                      <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ background: 'var(--primary)' }} />
                      {task}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-3" style={{ color: 'var(--primary)' }}>
                  {t('job.requirementsTitle')}
                </h4>
                <ul className="space-y-2">
                  {(t('job.requirements') || []).map((req, index) => (
                    <li key={index} className="flex items-start gap-2" style={{ color: 'var(--text-secondary)' }}>
                      <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ background: 'var(--primary)' }} />
                      {req}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-3" style={{ color: 'var(--primary)' }}>
                  {t('job.benefitsTitle')}
                </h4>
                <ul className="space-y-2">
                  {(t('job.benefits') || []).map((benefit, index) => (
                    <li key={index} className="flex items-start gap-2" style={{ color: 'var(--text-secondary)' }}>
                      <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ background: 'var(--primary)' }} />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-8 text-center">
              <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
                {t('job.interested')}
              </p>
              <a 
                href={`mailto:${CONTACT.jobEmail}?subject=Bewerbung als Zahnmedizinische Fachangestellte`}
                className="btn-primary w-full justify-center"
                data-testid="job-apply-btn"
              >
                {t('job.applyNow')}
              </a>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// Google Reviews and Social Media Section
const ReviewsSection = () => {
  const { t } = useLanguage();
  
  return (
    <section 
      id="bewertungen" 
      className="section"
      style={{ background: 'var(--background-slide-3)' }}
      aria-labelledby="reviews-title"
    >
      <div className="container mx-auto">
        <div className="text-center mb-12">
          <p className="uppercase text-xs tracking-widest font-semibold mb-2" style={{ color: 'var(--secondary)' }}>
            {t('reviews.subtitle')}
          </p>
          <h2 
            id="reviews-title"
            className="text-3xl md:text-4xl font-medium tracking-tight mb-4"
            style={{ color: 'var(--primary)' }}
          >
            {t('reviews.title')}
          </h2>
          
          {/* Google Rating Display */}
          <div className="flex items-center justify-center gap-2 mb-8">
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4].map((star) => (
                <Star 
                  key={star} 
                  className="w-6 h-6 fill-yellow-400 text-yellow-400" 
                />
              ))}
              {/* Partial star for 4.7 rating */}
              <div className="relative w-6 h-6">
                <Star className="w-6 h-6 text-gray-300 absolute" />
                <div className="overflow-hidden absolute" style={{ width: '70%' }}>
                  <Star className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                </div>
              </div>
            </div>
            <span className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
              4.7
            </span>
            <span style={{ color: 'var(--text-secondary)' }}>
              {t('reviews.onGoogle')}
            </span>
          </div>
        </div>

        {/* Sample Reviews Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-12 max-w-5xl mx-auto">
          {/* Review 1 - Neueste */}
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center text-white font-semibold">
                S
              </div>
              <div>
                <p className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>Sabine W.</p>
                <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>vor 2 Wochen</p>
                <div className="flex gap-0.5">
                  {[1,2,3,4,5].map(s => <Star key={s} className="w-3 h-3 fill-yellow-400 text-yellow-400" />)}
                </div>
              </div>
            </div>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              "Absolut empfehlenswert! Dr. Muresan-Richter hat mir bei akuten Zahnschmerzen sofort geholfen. Sehr einfühlsam und professionell. Die beste Zahnarztpraxis in Dachau!"
            </p>
          </div>

          {/* Review 2 */}
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold">
                M
              </div>
              <div>
                <p className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>Markus H.</p>
                <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>vor 1 Monat</p>
                <div className="flex gap-0.5">
                  {[1,2,3,4,5].map(s => <Star key={s} className="w-3 h-3 fill-yellow-400 text-yellow-400" />)}
                </div>
              </div>
            </div>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              "Moderne Praxis, super freundliches Team! Die PZR war gründlich und angenehm. Endlich ein Zahnarzt in Dachau Ost, bei dem man sich wohl fühlt. Klare Empfehlung!"
            </p>
          </div>

          {/* Review 3 */}
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white font-semibold">
                E
              </div>
              <div>
                <p className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>Elena K.</p>
                <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>vor 1 Monat</p>
                <div className="flex gap-0.5">
                  {[1,2,3,4,5].map(s => <Star key={s} className="w-3 h-3 fill-yellow-400 text-yellow-400" />)}
                </div>
              </div>
            </div>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              "Ich hatte große Angst vorm Zahnarzt, aber hier wurde ich so liebevoll behandelt! Das ganze Team ist toll. Termine bekommt man schnell, auch im Notfall. Danke!"
            </p>
          </div>
        </div>

        {/* Reviews CTA */}
        <div className="max-w-2xl mx-auto bg-white rounded-3xl p-8 shadow-lg text-center mb-16">
          <div className="flex justify-center mb-6">
            <svg className="w-16 h-16" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
              <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
              <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
              <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
              <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
            </svg>
          </div>
          
          <h3 className="text-xl font-semibold mb-4" style={{ color: 'var(--primary)' }}>
            {t('reviews.yourOpinion')}
          </h3>
          <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>
            {t('reviews.satisfied')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href={CONTACT.googleReviews}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center justify-center gap-2"
              data-testid="google-review-btn"
            >
              <Star className="w-5 h-5" />
              {t('reviews.rateNow')}
            </a>
            <a 
              href={CONTACT.googleMaps}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary inline-flex items-center justify-center gap-2"
              data-testid="google-maps-btn"
            >
              <ExternalLink className="w-5 h-5" />
              {t('reviews.viewOnMaps')}
            </a>
          </div>
        </div>

        {/* Social Media Section */}
        <div className="text-center">
          <h3 className="text-2xl font-medium mb-8" style={{ color: 'var(--primary)' }}>
            {t('reviews.followUs')}
          </h3>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Facebook Card */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center">
                  <Facebook className="w-6 h-6 text-white" />
                </div>
                <span className="font-semibold text-lg" style={{ color: 'var(--text-primary)' }}>
                  Facebook
                </span>
              </div>
              <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                {t('reviews.fbNews')}
              </p>
              <a 
                href={CONTACT.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary w-full justify-center text-sm py-2"
                data-testid="facebook-follow-btn"
              >
                <Facebook className="w-4 h-4" />
                {t('reviews.followFb')}
              </a>
            </div>

            {/* Instagram Card */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 flex items-center justify-center">
                  <Instagram className="w-6 h-6 text-white" />
                </div>
                <span className="font-semibold text-lg" style={{ color: 'var(--text-primary)' }}>
                  Instagram
                </span>
              </div>
              <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                {t('reviews.igStories')}
              </p>
              <a 
                href={CONTACT.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary w-full justify-center text-sm py-2"
                data-testid="instagram-follow-btn"
              >
                <Instagram className="w-4 h-4" />
                {t('reviews.followIg')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Location Section
const LocationSection = () => {
  const { t } = useLanguage();
  const { contact } = useData();
  
  // Use backend data or fallback to CONTACT constant
  const contactData = contact || CONTACT;
  
  // Parse opening hours - handle both array and object format
  const getOpeningHours = () => {
    if (!contactData.opening_hours && !contactData.hours) {
      return [];
    }
    const hours = contactData.opening_hours || contactData.hours;
    if (Array.isArray(hours)) {
      return hours;
    }
    // Convert object to array
    return Object.entries(hours).map(([key, value]) => ({
      id: key,
      label: key,
      value: value
    }));
  };
  
  const openingHours = getOpeningHours();
  
  const downloadVCard = () => {
    const phone = contactData.phone || CONTACT.phone;
    const email = contactData.email || CONTACT.email;
    const address = contactData.address || CONTACT.address;
    const name = contactData.practice_name || CONTACT.name;
    
    const vcard = `BEGIN:VCARD
VERSION:3.0
FN:${name}
ORG:Zahnarztpraxis Muresan-Richter
TEL;TYPE=WORK,VOICE:${phone.replace(/[^\d+]/g, '')}
EMAIL:${email}
ADR;TYPE=WORK:;;${address}
URL:https://www.zahnarzt-muresan.de
END:VCARD`;
    
    const blob = new Blob([vcard], { type: 'text/vcard' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'zahnarztpraxis-muresan-richter.vcf';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section 
      id="anfahrt" 
      className="section"
      style={{ background: 'var(--background-slide-2)' }}
      aria-labelledby="location-title"
    >
      <div className="container mx-auto">
        <div className="text-center mb-12">
          <p className="uppercase text-xs tracking-widest font-semibold mb-2" style={{ color: 'var(--secondary)' }}>
            {t('location.subtitle')}
          </p>
          <h2 
            id="location-title"
            className="text-3xl md:text-4xl font-medium tracking-tight"
            style={{ color: 'var(--primary)' }}
          >
            {t('location.title')}
          </h2>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-8 items-stretch">
          {/* Map */}
          <div className="h-full">
            <div className="map-container shadow-lg h-full" style={{ minHeight: '450px' }}>
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2654.5!2d11.4307!3d48.2595!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x479e7b8f5c5b5b5b%3A0x5b5b5b5b5b5b5b5b!2sStresemannstra%C3%9Fe%2013%2C%2085221%20Dachau%2C%20Germany!5e0!3m2!1sde!2sde!4v1703514400000!5m2!1sde!2sde"
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Standort der Zahnarztpraxis Muresan-Richter auf Google Maps"
                data-testid="google-map"
                className="w-full h-full"
                style={{ minHeight: '450px' }}
              />
            </div>
          </div>

          {/* Contact Info */}
          <div className="bg-white rounded-2xl p-8 shadow-lg h-full flex flex-col">
            <h3 className="text-xl font-semibold mb-6" style={{ color: 'var(--primary)' }}>
              {t('location.contactData')}
            </h3>
            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: 'var(--primary)' }} />
                <div>
                  <p className="font-medium" style={{ color: 'var(--text-primary)' }}>{t('location.address')}</p>
                  <p style={{ color: 'var(--text-secondary)' }}>{contactData.address || CONTACT.address}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: 'var(--primary)' }} />
                <div>
                  <p className="font-medium" style={{ color: 'var(--text-primary)' }}>{t('location.phone')}</p>
                  <a 
                    href={`tel:${(contactData.phone || CONTACT.phone).replace(/\s/g, '')}`}
                    className="hover:underline"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {contactData.phone || CONTACT.phone}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: 'var(--primary)' }} />
                <div>
                  <p className="font-medium" style={{ color: 'var(--text-primary)' }}>E-Mail</p>
                  <a 
                    href={`mailto:${contactData.email || CONTACT.email}`}
                    className="hover:underline"
                    style={{ color: 'var(--text-secondary)' }}
                    data-testid="contact-email"
                  >
                    {contactData.email || CONTACT.email}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: 'var(--primary)' }} />
                <div>
                  <p className="font-medium mb-2" style={{ color: 'var(--text-primary)' }}>{t('location.openingHours')}</p>
                  <div className="text-sm space-y-1" style={{ color: 'var(--text-secondary)' }}>
                    {openingHours.length > 0 ? (
                      openingHours.map((hour, index) => (
                        <p key={hour.id || index}>{hour.value}</p>
                      ))
                    ) : (
                      <>
                        <p>Mo: 08:30 - 12:00, 14:00 - 18:00 Uhr</p>
                        <p>Di: 08:30 - 12:00, 14:00 - 18:00 Uhr</p>
                        <p>Mi: 08:30 - 12:00, 14:00 - 18:00 Uhr</p>
                        <p>Do: 08:30 - 12:00, 14:00 - 18:00 Uhr</p>
                        <p>Fr: 08:30 - 12:00 Uhr</p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t pt-6 mb-6" style={{ borderColor: 'var(--primary-light)' }}>
              <h4 className="font-medium mb-3" style={{ color: 'var(--text-primary)' }}>{t('location.directions')}</h4>
              <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>
                <strong>{t('location.byCar')}</strong> {t('location.byCarText')}
              </p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                <strong>{t('location.publicTransport')}</strong> {t('location.publicTransportText')}
              </p>
            </div>

            <button 
              onClick={downloadVCard}
              className="w-full py-3 px-4 rounded-full border-2 font-semibold flex items-center justify-center gap-2 transition-colors hover:bg-gray-50"
              style={{ borderColor: 'var(--primary)', color: 'var(--primary)' }}
              data-testid="download-vcard"
            >
              <Download className="w-5 h-5" />
              {t('location.saveContact')}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useLanguage();

  return (
    <footer className="footer" role="contentinfo">
      <div className="container mx-auto">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          {/* Logo & Info */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img 
                src={ASSETS.logo} 
                alt="" 
                className="h-12 w-12 rounded-full bg-white p-1"
                aria-hidden="true"
              />
              <span className="text-xl font-semibold">
                Zahnarztpraxis Muresan-Richter
              </span>
            </div>
            <p className="opacity-80 mb-4 max-w-md">
              {t('footer.tagline')}
            </p>
            <a 
              href={CONTACT.doctolib}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white font-semibold px-6 py-3 rounded-full hover:opacity-90 transition-opacity"
              style={{ color: 'var(--primary)' }}
              data-testid="footer-booking-btn"
            >
              {t('footer.bookAppointment')}
            </a>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-lg mb-4">{t('footer.quickLinks')}</h3>
            <ul className="space-y-2">
              <li><a href="/#kontakt" className="opacity-80 hover:opacity-100" data-testid="footer-kontakt">{t('nav.contact')}</a></li>
              <li><a href="/#behandlungsfelder" className="opacity-80 hover:opacity-100" data-testid="footer-behandlungsfelder">{t('nav.treatments')}</a></li>
              <li><a href="/#team" className="opacity-80 hover:opacity-100" data-testid="footer-team">{t('nav.team')}</a></li>
              <li><a href="/#anfahrt" className="opacity-80 hover:opacity-100" data-testid="footer-anfahrt">{t('nav.directions')}</a></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="font-semibold text-lg mb-4">{t('footer.legal')}</h3>
            <ul className="space-y-2">
              <li><Link to="/impressum" className="opacity-80 hover:opacity-100" data-testid="footer-impressum">{t('footer.imprint')}</Link></li>
              <li><Link to="/datenschutz" className="opacity-80 hover:opacity-100" data-testid="footer-datenschutz">{t('footer.privacy')}</Link></li>
              <li><Link to="/admin" className="opacity-40 hover:opacity-60 text-xs" style={{ color: '#1B4D3E' }} data-testid="footer-admin">Muresan</Link></li>
            </ul>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-white/20 pt-8 text-center text-sm opacity-70">
          <p>&copy; {currentYear} Zahnarztpraxis Muresan-Richter. {t('footer.copyright')}</p>
        </div>
      </div>
    </footer>
  );
};

// Home Page
const Home = () => {
  const { showBanner } = useCookies();

  return (
    <>
      {showBanner && <CookieConsent />}
      <NewsPopup />
      <Header />
      <main id="main-content">
        <HeroSection />
        <AboutSection />
        <TreatmentSlider />
        <TeamSection />
        <ReviewsSection />
        <LocationSection />
      </main>
      <Footer />
    </>
  );
};

// Impressum Page
const Impressum = () => {
  return (
    <>
      <Header />
      <main id="main-content" className="pt-32 pb-20">
        <div className="container mx-auto px-4 md:px-8">
          <article className="prose mx-auto">
            <h1 style={{ color: 'var(--primary)' }}>Impressum</h1>
            
            <h2>Angaben gemäß § 5 TMG:</h2>
            <p>
              Andrei Günter Muresan-Richter<br />
              Stresemannstraße 13<br />
              85221 Dachau
            </p>
            <p>
              Tel: 0 81 31 - 12 12 2<br />
              E-Mail: info@zahnarzt-muresan.de
            </p>
            <p>
              <em>(Ich weise vorsorglich darauf hin, dass über die genannte E-Mail-Adresse keine Beratung zu medizinischen Behandlungen erfolgt.)</em>
            </p>

            <h2>Zuständige Aufsichtsbehörde:</h2>
            <p>
              Kassenzahnärztliche Vereinigung Bayerns<br />
              Fallstraße 34<br />
              81369 München<br />
              Tel: +49 (0) 89 72 40 10
            </p>

            <h2>Gesetzliche Berufsbezeichnung:</h2>
            <p>
              Andrei Muresan ist auf der Grundlage der Gesetze der Bundesrepublik Deutschland als Zahnarzt zugelassen.
            </p>

            <h2>Berufsrechtliche Regelung:</h2>
            <ul>
              <li>Zahnheilkundegesetz (BGBI. I 1952, S. 125)</li>
              <li>Heilberufe-Kammergesetz</li>
              <li>Gebührenordnung für Zahnärzte (BGBI. I 2011, S. 2661)</li>
              <li>Berufsordnung für Zahnärzte, abrufbar unter www.zaek-berlin.de</li>
            </ul>

            <h2>Zuständige Landesärztekammer (BLZK)</h2>
            <p>
              Bayerische Landeszahnärztekammer<br />
              Postfach 70 10 68<br />
              81369 München<br />
              Tel: +49 (0) 89 23 02 11-0
            </p>
          </article>
        </div>
      </main>
      <Footer />
    </>
  );
};

// Datenschutz Page
const Datenschutz = () => {
  return (
    <>
      <Header />
      <main id="main-content" className="pt-32 pb-20">
        <div className="container mx-auto px-4 md:px-8">
          <article className="prose mx-auto max-w-4xl">
            <h1 style={{ color: 'var(--primary)' }}>Datenschutzerklärung</h1>
            
            <h2>Allgemeine Hinweise</h2>
            <p>
              Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie unsere Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können. Ausführliche Informationen zum Thema Datenschutz entnehmen Sie unserer unter diesem Text aufgeführten Datenschutzerklärung.
            </p>

            <h2>Datenerfassung auf unserer Website</h2>
            
            <h3>Wer ist verantwortlich für die Datenerfassung auf dieser Website?</h3>
            <p>
              Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen Kontaktdaten können Sie dem Impressum dieser Website entnehmen.
            </p>

            <h3>Wie erfassen wir Ihre Daten?</h3>
            <p>
              Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. Hierbei kann es sich z. B. um Daten handeln, die Sie in ein Kontaktformular eingeben.
            </p>
            <p>
              Andere Daten werden automatisch beim Besuch der Website durch unsere IT-Systeme erfasst. Das sind vor allem technische Daten (z. B. Internetbrowser, Betriebssystem oder Uhrzeit des Seitenaufrufs). Die Erfassung dieser Daten erfolgt automatisch, sobald Sie unsere Website betreten.
            </p>

            <h3>Wofür nutzen wir Ihre Daten?</h3>
            <p>
              Ein Teil der Daten wird erhoben, um eine fehlerfreie Bereitstellung der Website zu gewährleisten. Andere Daten können zur Analyse Ihres Nutzerverhaltens verwendet werden.
            </p>

            <h3>Welche Rechte haben Sie bezüglich Ihrer Daten?</h3>
            <p>
              Sie haben jederzeit das Recht unentgeltlich Auskunft über Herkunft, Empfänger und Zweck Ihrer gespeicherten personenbezogenen Daten zu erhalten. Sie haben außerdem ein Recht, die Berichtigung, Sperrung oder Löschung dieser Daten zu verlangen. Hierzu sowie zu weiteren Fragen zum Thema Datenschutz können Sie sich jederzeit unter der im Impressum angegebenen Adresse an uns wenden. Des Weiteren steht Ihnen ein Beschwerderecht bei der zuständigen Aufsichtsbehörde zu.
            </p>
            <p>
              Außerdem haben Sie das Recht, unter bestimmten Umständen die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten zu verlangen. Details hierzu entnehmen Sie der Datenschutzerklärung unter „Recht auf Einschränkung der Verarbeitung".
            </p>

            <h3>Analyse-Tools und Tools von Drittanbietern</h3>
            <p>
              Beim Besuch unserer Website kann Ihr Surf-Verhalten statistisch ausgewertet werden. Das geschieht vor allem mit Cookies und mit sogenannten Analyseprogrammen. Die Analyse Ihres Surf-Verhaltens erfolgt in der Regel anonym; das Surf-Verhalten kann nicht zu Ihnen zurückverfolgt werden.
            </p>
            <p>
              Sie können dieser Analyse widersprechen oder sie durch die Nichtbenutzung bestimmter Tools verhindern. Detaillierte Informationen zu diesen Tools und über Ihre Widerspruchsmöglichkeiten finden Sie in der folgenden Datenschutzerklärung.
            </p>

            <h2>Allgemeine Hinweise und Pflichtinformationen</h2>
            
            <h3>Datenschutz</h3>
            <p>
              Die Betreiber dieser Seiten nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir behandeln Ihre personenbezogenen Daten vertraulich und entsprechend der gesetzlichen Datenschutzvorschriften sowie dieser Datenschutzerklärung. Wenn Sie diese Website benutzen, werden verschiedene personenbezogene Daten erhoben. Personenbezogene Daten sind Daten, mit denen Sie persönlich identifiziert werden können. Die vorliegende Datenschutzerklärung erläutert, welche Daten wir erheben und wofür wir sie nutzen. Sie erläutert auch, wie und zu welchem Zweck das geschieht. Wir weisen darauf hin, dass die Datenübertragung im Internet (z. B. bei der Kommunikation per E-Mail) Sicherheitslücken aufweisen kann. Ein lückenloser Schutz der Daten vor dem Zugriff durch Dritte ist nicht möglich.
            </p>

            <h3>Hinweis zur verantwortlichen Stelle</h3>
            <p>
              Die verantwortliche Stelle für die Datenverarbeitung auf dieser Website ist:
            </p>
            <p>
              Zahnarztpraxis Andrei Günter Muresan-Richter<br />
              Telefon: +49 (0) 8131 / 12122<br />
              E-Mail: datenschutz@zahnarzt-muresan.de
            </p>
            <p>
              Verantwortliche Stelle ist die natürliche oder juristische Person, die allein oder gemeinsam mit anderen über die Zwecke und Mittel der Verarbeitung von personenbezogenen Daten (z. B. Namen, E-Mail-Adressen o. Ä.) entscheidet.
            </p>

            <h3>Widerruf Ihrer Einwilligung zur Datenverarbeitung</h3>
            <p>
              Viele Datenverarbeitungsvorgänge sind nur mit Ihrer ausdrücklichen Einwilligung möglich. Sie können eine bereits erteilte Einwilligung jederzeit widerrufen. Dazu reicht eine formlose Mitteilung per E-Mail an uns. Die Rechtmäßigkeit der bis zum Widerruf erfolgten Datenverarbeitung bleibt vom Widerruf unberührt.
            </p>

            <h3>Widerspruchsrecht gegen die Datenerhebung in besonderen Fällen sowie gegen Direktwerbung (Art. 21 DSGVO)</h3>
            <p>
              Wenn die Datenverarbeitung auf Grundlage von Art. 6 Abs. 1 lit. e oder f DSGVO erfolgt, haben Sie jederzeit das Recht, aus Gründen, die sich aus Ihrer besonderen Situation ergeben, gegen die Verarbeitung Ihrer personenbezogenen Daten Widerspruch einzulegen; dies gilt auch für ein auf diese Bestimmungen gestütztes Profiling. Die jeweilige Rechtsgrundlage, auf denen eine Verarbeitung beruht, entnehmen Sie dieser Datenschutzerklärung. Wenn Sie Widerspruch einlegen, werden wir Ihre betroffenen personenbezogenen Daten nicht mehr verarbeiten, es sei denn, wir können zwingende schutzwürdige Gründe für die Verarbeitung nachweisen, die Ihre Interessen, Rechte und Freiheiten überwiegen oder die Verarbeitung dient der Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen (Widerspruch nach Art. 21 Abs. 1 DSGVO).
            </p>
            <p>
              Werden Ihre personenbezogenen Daten verarbeitet, um Direktwerbung zu betreiben, so haben Sie das Recht, jederzeit Widerspruch gegen die Verarbeitung Sie betreffender personenbezogener Daten zum Zwecke derartiger Werbung einzulegen; dies gilt auch für das Profiling, soweit es mit solcher Direktwerbung in Verbindung steht. Wenn Sie widersprechen, werden Ihre personenbezogenen Daten anschließend nicht mehr zum Zwecke der Direktwerbung verwendet (Widerspruch nach Art. 21 Abs. 2 DSGVO).
            </p>

            <h3>Beschwerderecht bei der zuständigen Aufsichtsbehörde</h3>
            <p>
              Im Falle von Verstößen gegen die DSGVO steht den Betroffenen ein Beschwerderecht bei einer Aufsichtsbehörde, insbesondere in dem Mitgliedstaat ihres gewöhnlichen Aufenthalts, ihres Arbeitsplatzes oder des Orts des mutmaßlichen Verstoßes zu. Das Beschwerderecht besteht unbeschadet anderweitiger verwaltungsrechtlicher oder gerichtlicher Rechtsbehelfe. Eine Liste der Datenschutzbeauftragten sowie deren Kontaktdaten können folgendem Link entnommen werden: <a href="https://www.bfdi.bund.de/DE/Service/Anschriften/anschriften_table.html" target="_blank" rel="noopener noreferrer">https://www.bfdi.bund.de</a> (Deutschland).
            </p>

            <h3>Recht auf Datenübertragbarkeit</h3>
            <p>
              Sie haben das Recht, Daten, die wir auf Grundlage Ihrer Einwilligung oder in Erfüllung eines Vertrags automatisiert verarbeiten, an sich oder an einen Dritten in einem gängigen, maschinenlesbaren Format aushändigen zu lassen. Sofern Sie die direkte Übertragung der Daten an einen anderen Verantwortlichen verlangen, erfolgt dies nur, soweit es technisch machbar ist.
            </p>

            <h3>SSL- bzw. TLS-Verschlüsselung</h3>
            <p>
              Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der Übertragung vertraulicher Inhalte, wie zum Beispiel Bestellungen oder Anfragen, die Sie an uns als Seitenbetreiber senden, eine SSL- bzw. TLS-Verschlüsselung. Eine verschlüsselte Verbindung erkennen Sie daran, dass die Adresszeile des Browsers von „http://" auf „https://" wechselt und an dem Schloss-Symbol in Ihrer Browserzeile.
            </p>
            <p>
              Wenn die SSL- bzw. TLS-Verschlüsselung aktiviert ist, können die Daten, die Sie an uns übermitteln, nicht von Dritten mitgelesen werden.
            </p>

            <h3>Auskunft, Sperrung, Löschung und Berichtigung</h3>
            <p>
              Sie haben im Rahmen der geltenden gesetzlichen Bestimmungen jederzeit das Recht auf unentgeltliche Auskunft über Ihre gespeicherten personenbezogenen Daten, deren Herkunft und Empfänger und den Zweck der Datenverarbeitung und ggf. ein Recht auf Berichtigung, Sperrung oder Löschung dieser Daten. Hierzu sowie zu weiteren Fragen zum Thema personenbezogene Daten können Sie sich jederzeit unter der im Impressum angegebenen Adresse an uns wenden.
            </p>

            <h3>Recht auf Einschränkung der Verarbeitung</h3>
            <p>
              Sie haben das Recht, die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten zu verlangen. Hierzu können Sie sich jederzeit unter der im Impressum angegebenen Adresse an uns wenden. Das Recht auf Einschränkung der Verarbeitung besteht in folgenden Fällen:
            </p>
            <ul>
              <li>Wenn Sie die Richtigkeit Ihrer bei uns gespeicherten personenbezogenen Daten bestreiten, benötigen wir in der Regel Zeit, um dies zu überprüfen. Für die Dauer der Prüfung haben Sie das Recht, die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten zu verlangen.</li>
              <li>Wenn die Verarbeitung Ihrer personenbezogenen Daten unrechtmäßig geschah/geschieht, können Sie statt der Löschung die Einschränkung der Datenverarbeitung verlangen.</li>
              <li>Wenn wir Ihre personenbezogenen Daten nicht mehr benötigen, Sie sie jedoch zur Ausübung, Verteidigung oder Geltendmachung von Rechtsansprüchen benötigen, haben Sie das Recht, statt der Löschung die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten zu verlangen.</li>
              <li>Wenn Sie einen Widerspruch nach Art. 21 Abs. 1 DSGVO eingelegt haben, muss eine Abwägung zwischen Ihren und unseren Interessen vorgenommen werden. Solange noch nicht feststeht, wessen Interessen überwiegen, haben Sie das Recht, die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten zu verlangen.</li>
            </ul>
            <p>
              Wenn Sie die Verarbeitung Ihrer personenbezogenen Daten eingeschränkt haben, dürfen diese Daten – von ihrer Speicherung abgesehen – nur mit Ihrer Einwilligung oder zur Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen oder zum Schutz der Rechte einer anderen natürlichen oder juristischen Person oder aus Gründen eines wichtigen öffentlichen Interesses der Europäischen Union oder eines Mitgliedstaats verarbeitet werden.
            </p>

            <h3>Widerspruch gegen Werbe-Mails</h3>
            <p>
              Der Nutzung von im Rahmen der Impressumspflicht veröffentlichten Kontaktdaten zur Übersendung von nicht ausdrücklich angeforderter Werbung und Informationsmaterialien wird hiermit widersprochen. Die Betreiber der Seiten behalten sich ausdrücklich rechtliche Schritte im Falle der unverlangten Zusendung von Werbeinformationen, etwa durch Spam-E-Mails, vor.
            </p>

            <h2>Datenerfassung auf unserer Website</h2>

            <h3>Cookies</h3>
            <p>
              Diese Website verwendet insbesondere Cookies oder andere Technologien, die unbedingt erforderlich sind bzw. solche wie funktionale Cookies, damit Ihnen die abgerufene Website nebst Funktionen zur Verfügung gestellt werden kann, § 25 Abs. 2 Ziffer 2 Telekommunikation-Digitale-Dienste-Datenschutz-Gesetz (TDDDG), Artikel 6 Abs. 1 Unterabsatz 1 Buchstabe f DSGVO.
            </p>
            <p>
              Soweit Sie für optionale Dienste, nicht erforderliche Cookies die Einwilligung erteilen, ist Rechtsgrundlage § 25 Abs. 1 TDDDG, Artikel 6 Abs. 1 Unterabsatz 1 Buchstabe a DSGVO (Einwilligung). Hierzu und zu den eingesetzten Cookies bzw. Diensten können Sie jederzeit weitere Informationen aus dem eingesetzten Consent-Management-Tool entnehmen und Einwilligungen jederzeit frei und ohne Nachteil mit Wirkung für die Zukunft widerrufen. Bitte beachten Sie aber, dass unsere Website ohne die verwendeten Cookies nicht immer so funktioniert wie vorgesehen.
            </p>
            <p>
              Die eingesetzten Cookies können Sie in unserem Tool einsehen sowie jederzeit Ihre etwaigen Einwilligungen etc. dort bearbeiten und widerrufen. Durch den Widerruf der Einwilligung wird die Rechtmäßigkeit der aufgrund der Einwilligung bis zum Widerruf erfolgten Verarbeitung nicht berührt.
            </p>

            <h3>Server-Log-Dateien</h3>
            <p>
              Der Provider der Seiten erhebt und speichert automatisch Informationen in so genannten Server-Log-Dateien, die Ihr Browser automatisch an uns übermittelt. Dies sind:
            </p>
            <ul>
              <li>Browsertyp und Browserversion</li>
              <li>verwendetes Betriebssystem</li>
              <li>Referrer URL</li>
              <li>Hostname des zugreifenden Rechners</li>
              <li>Uhrzeit der Serveranfrage</li>
              <li>IP-Adresse</li>
            </ul>
            <p>
              Eine Zusammenführung dieser Daten mit anderen Datenquellen wird nicht vorgenommen. Die Erfassung dieser Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO. Der Websitebetreiber hat ein berechtigtes Interesse an der technisch fehlerfreien Darstellung und der Optimierung seiner Website – hierzu müssen die Server-Log-Files erfasst werden.
            </p>

            <h3>Anfrage per E-Mail, Telefon oder Telefax</h3>
            <p>
              Wenn Sie uns per E-Mail, Telefon oder Telefax kontaktieren, wird Ihre Anfrage inklusive aller daraus hervorgehenden personenbezogenen Daten (Name, Anfrage) zum Zwecke der Bearbeitung Ihres Anliegens bei uns gespeichert und verarbeitet. Diese Daten geben wir nicht ohne Ihre Einwilligung weiter.
            </p>
            <p>
              Die Verarbeitung dieser Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO, sofern Ihre Anfrage mit der Erfüllung eines Vertrags zusammenhängt oder zur Durchführung vorvertraglicher Maßnahmen erforderlich ist. In allen übrigen Fällen beruht die Verarbeitung auf Ihrer Einwilligung (Art. 6 Abs. 1 lit. a DSGVO) und/oder auf unseren berechtigten Interessen (Art. 6 Abs. 1 lit. f DSGVO), da wir ein berechtigtes Interesse an der effektiven Bearbeitung der an uns gerichteten Anfragen haben.
            </p>

            <h2>Plugins und Tools</h2>

            <h3>Google Web Fonts</h3>
            <p>
              Diese Seite nutzt zur einheitlichen Darstellung von Schriftarten so genannte Web Fonts, die von Google bereitgestellt werden. Die Google Fonts sind lokal installiert. Eine Verbindung zu Servern von Google findet dabei nicht statt.
            </p>

            <h3>Google Maps</h3>
            <p>
              Diese Seite nutzt über eine API den Kartendienst Google Maps. Anbieter ist die Google Ireland Limited („Google"), Gordon House, Barrow Street, Dublin 4, Irland.
            </p>
            <p>
              Zur Nutzung der Funktionen von Google Maps ist es notwendig, Ihre IP Adresse zu speichern. Diese Informationen werden in der Regel an einen Server von Google in den USA übertragen und dort gespeichert. Der Anbieter dieser Seite hat keinen Einfluss auf diese Datenübertragung.
            </p>
            <p>
              Die Nutzung von Google Maps erfolgt im Interesse einer ansprechenden Darstellung unserer Online-Angebote und an einer leichten Auffindbarkeit der von uns auf der Website angegebenen Orte. Dies stellt ein berechtigtes Interesse im Sinne von Art. 6 Abs. 1 lit. f DSGVO dar. Im Übrigen werden die Google Dienste nur aktiv geladen, wenn Sie Ihre Einwilligung hierzu zuvor erteilten, Art. 6 Abs. 1 lit. a DSGVO.
            </p>
            <p>
              Mehr Informationen zum Umgang mit Nutzerdaten finden Sie in der Datenschutzerklärung von Google: <a href="https://policies.google.com/privacy?hl=de" target="_blank" rel="noopener noreferrer">https://policies.google.com/privacy?hl=de</a>.
            </p>

            <h2>Speicherdauer</h2>
            <p>
              Gespeicherte Server-Logfiles und IP-Adressen werden spätestens nach sieben Tagen gelöscht.
            </p>
            <p>
              Session-Cookies werden nach Beendigung der Sitzung automatisch gelöscht. Andere Cookies werden auf Ihrem Endgerät gespeichert, und Sie haben die Kontrolle über die Verwendung und Löschung von Cookies.
            </p>
            <p>
              Ihre Daten aus Ihren Anfragen via E-Mail oder via Kontaktformular verarbeiten wir, bis Ihre Anfrage vollständig bearbeitet und erledigt ist. Danach werden die Angaben gelöscht. Bitte beachten Sie aber, dass wegen eines Rechtsgeschäfts mit Ihnen für bestimmte Daten handels- und steuerrechtliche Aufbewahrungspflichten von mindestens sechs (§ 257 HGB) oder zehn (§147 AO) Jahren bestehen können.
            </p>

            <p className="text-sm mt-12" style={{ color: 'var(--text-secondary)' }}>
              <em>Stand: Dezember 2025</em>
            </p>
          </article>
        </div>
      </main>
      <Footer />
    </>
  );
};

// Main App
function App() {
  return (
    <LanguageProvider>
      <DataProvider>
        <CookieProvider>
          <div className="App">
            <a href="#main-content" className="skip-link">
              Zum Hauptinhalt springen
            </a>
            <BrowserRouter>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/impressum" element={<Impressum />} />
                <Route path="/datenschutz" element={<Datenschutz />} />
                <Route path="/admin/*" element={<AdminApp />} />
              </Routes>
            </BrowserRouter>
          </div>
        </CookieProvider>
      </DataProvider>
    </LanguageProvider>
  );
}

export default App;
